/**
 * El aviso del estado de la cuenta de Stripe.
 *
 * Vive aparte porque sale en dos sitios —encima de la lista de ofertas y encima
 * de una ficha— y son el mismo aviso, no dos parecidos. Cuando estaba escrito
 * dos veces, la ficha decía una cosa y la lista otra con las mismas palabras
 * cambiadas de sitio, que es exactamente como empiezan a contradecirse.
 *
 * No bloquea nada: dice qué falta para poder cobrar y lleva a donde se arregla.
 * Toda la tienda se monta sin cuenta conectada —crear ofertas, escribirlas,
 * ponerles precio—, y decirlo es la mitad del mensaje. Lo que no se puede sin
 * ella es PUBLICARLAS.
 *
 * En el plan gratis no sale un aviso sino la tarjeta de venta de véltica:
 * conectar Stripe pide membresía, así que mandar a alguien al alta para que se
 * topara allí con la misma reja sería hacerle dar el viaje en balde. Y contado
 * como aviso —«conectar Stripe requiere un plan de pago»— la pantalla ponía por
 * delante el trámite de un tercero en vez de lo que se gana pagando.
 */
import { useState } from 'react'
import { StripeMark } from './StripeMark'
import { PAYMENTS_UPSELL, UpsellCard } from '../FeatureUpsell'
import type { ConnectStatus } from '../../../lib/api'
import { IconArrowUpRight } from '@tabler/icons-react'
import { Notice } from '../Notice'
import { useI18n } from '../../../lib/i18n'
import { closeNoticeForSession, dismissNotice, isNoticeHidden } from '../../../lib/dismissedNotices'

/**
 * Titular y detalle de cada estado.
 *
 * `ready` está en el mapa por completitud del tipo; con la cuenta lista no hay
 * aviso ninguno. El titular dice qué falta, no qué hacer — la acción ya es un
 * botón al lado.
 */
const BANNER_COPY: Record<ConnectStatus, { title: string; detail: string }> = {
  // Dice que se puede seguir trabajando porque es verdad y porque, sin decirlo,
  // la pantalla parecía pedir el trámite antes de dejar empezar.
  none: { title: 'stripe.banner.none.title', detail: 'stripe.banner.none.detail' },
  pending: { title: 'stripe.banner.pending.title', detail: 'stripe.banner.pending.detail' },
  restricted: { title: 'stripe.banner.restricted.title', detail: 'stripe.banner.restricted.detail' },
  ready: { title: 'stripe.banner.ready.title', detail: 'stripe.banner.ready.detail' },
}

export function StripeConnectBanner({ status, failed = false, detail, canConnect = true, hasPaidOffer = true, onConnect, onOpenPlans, onRetry, reveal = false, overContent = false }: {
  /**
   * `null` mientras no se sabe. No se pinta nada en ese caso: al llegar por
   * enlace la cuenta aún viaja, y dar por hecho que no hay ninguna hacía
   * parpadear el aviso a quien sí la tiene conectada.
   */
  status: ConnectStatus | null
  /** La consulta falló. Entonces no se afirma nada sobre la cuenta. */
  failed?: boolean
  /** El motivo técnico del fallo, para poder copiarlo. */
  detail?: string | undefined
  /**
   * Si el plan permite darse de alta. Sólo cambia lo que se dice y a dónde
   * lleva el botón —la reja de verdad la pone el servidor, en
   * `assertCanConnectStripe`—, pero decirlo aquí es lo que evita el viaje.
   *
   * No mira al alta a medias: quien ya tiene cuenta la conserva aunque baje de
   * plan, así que ahí el camino sigue siendo terminar el trámite.
   */
  canConnect?: boolean
  onConnect: () => void
  /** A dónde se manda a quien todavía no puede conectar. */
  onOpenPlans?: (() => void) | undefined
  onRetry?: (() => void) | undefined
  /**
   * Que entre abriendo su altura, donde llegue después que la pantalla.
   *
   * Sólo Depósitos. En Ofertas y Cobros la espera cuenta también la consulta
   * del estado, así que el aviso está desde el primer fotograma; allí un gesto
   * suyo llamaría la atención sobre algo que ya estaba. En Depósitos, lo que va
   * debajo es un marco de Stripe que monta cuando le parece, y no se puede
   * esperar a que termine para pintar la pantalla.
   */
  reveal?: boolean
  /**
   * Si debajo hay trabajo que hacer.
   *
   * En Cobros y en la ficha de una oferta, sí: el catálogo se monta igual sin
   * cuenta que cobre, así que la oferta de plan va encima de él y en corto. En
   * Depósitos no hay nada debajo que no dependa de poder cobrar, y ahí la
   * tarjeta es la pantalla.
   */
  overContent?: boolean
  /**
   * Si hay algo que cobrar. Sin cuenta conectada, el aviso sólo sale cuando lo
   * hay: pedir Stripe a quien no vende nada es un trámite sin motivo. `null`
   * mientras se cargan las ofertas, que también lo calla — dar por hecho que no
   * hay ninguna o que sí haría parpadear el aviso.
   *
   * Con un alta a medias sale igual: ahí ya se empezó, y lo que falta es
   * terminarla. Donde el aviso ES la pantalla (Depósitos) no se pasa.
   */
  hasPaidOffer?: boolean | null
}) {
  const { t } = useI18n()
  /*
   * «Cerrar» y «No volver a mostrar», sólo para la tarjeta de venta encima del
   * trabajo.
   *
   * Ahí se ve cada vez que se entra a la lista, y quien ya decidió no pagar
   * todavía la tenía delante del catálogo para siempre. Donde la tarjeta ES la
   * pantalla —Depósitos— no se quita: dejaría la pantalla vacía sin decir por
   * qué. Por persona y no por proyecto; ver `dismissedNotices`.
   */
  const [upsellHidden, setUpsellHidden] = useState(() => isNoticeHidden('payments-upsell'))
  if (!failed && (status === null || status === 'ready')) return null
  if (!failed && status === 'none' && hasPaidOffer !== true) return null

  /* La reja del plan sólo se enseña donde aplica: sin cuenta ninguna. Con un
     alta a medias no hay nada que vender —esa cuenta ya existe— y el aviso
     vuelve a ser el de terminar el trámite. */
  const planLocked = !failed && status === 'none' && !canConnect
  /*
   * Y cuando lo que falta es el plan, esto no es un aviso: es una oferta.
   *
   * Como aviso empezaba por «conectar Stripe» —el trámite, no lo que se gana
   * con él— y con el mismo aspecto que «te falta terminar el alta», es decir,
   * algo pendiente por tu culpa. Se usa la misma tarjeta que Analíticas y
   * Colaboradores: las tres veces que este panel pide dinero se reconocen como
   * la misma voz.
   */
  if (planLocked && onOpenPlans) {
    if (overContent && upsellHidden) return null
    return (
      <UpsellCard
        standalone
        slim={overContent}
        copy={PAYMENTS_UPSELL}
        onOpenPlans={onOpenPlans}
        {...(overContent ? {
          dismiss: {
            onClose: () => {
              closeNoticeForSession('payments-upsell')
              setUpsellHidden(true)
            },
            onNeverShow: () => {
              dismissNotice('payments-upsell')
              setUpsellHidden(true)
            },
          },
        } : {})}
      />
    )
  }

  const copy = BANNER_COPY[status ?? 'none']
  // Sin cuenta va en el morado de Stripe: no hay nada roto ni nada que
  // corregir, es una invitación. El ámbar queda para el alta a medias, que sí
  // pide volver, y el rojo para cuando algo falla.
  const tone = failed ? 'error' : status === 'none' ? 'brand' : 'warn'

  return (
    <Notice
      tone={tone}
      reveal={reveal}
      {...(tone === 'brand' ? { icon: <StripeMark size={17} /> } : {})}
      title={t(failed ? 'stripe.banner.failedTitle' : copy.title)}
      actions={failed
        ? (onRetry ? <button type="button" className="dash-secondary-btn" onClick={onRetry}>{t('stripe.retry')}</button> : null)
        : (
          <button type="button" className="dash-primary-btn dash-notice-cta" onClick={onConnect}>
            <StripeMark size={14} />
            {t(status === 'none' ? 'stripe.connect' : 'stripe.continueSetup')}
            <IconArrowUpRight size={14} />
          </button>
        )}
    >
      <p>
        {t(failed ? 'stripe.banner.failedBody' : copy.detail)}
      </p>
      {failed && detail ? <code className="dash-failure-detail">{detail}</code> : null}
    </Notice>
  )
}
